<script setup lang="ts">
import type { VaultFileSignatureInfoInDb } from '@/api';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';

/** The signatures detected on a revision, as a titled list — tooltip content. */
defineProps<{
  signatures: VaultFileSignatureInfoInDb[];
}>();

type SignatureLevel = VaultFileSignatureInfoInDb['type'];

const TYPE_LABEL: Record<SignatureLevel, string> = {
  simple: 'Simple',
  advanced: 'Advanced',
  qualified: 'Qualified',
};

/**
 * The three eIDAS assurance levels, as design-system chip tones. Qualified is
 * the legally strongest, so it alone gets the success tone; the other two read
 * as statements of fact rather than as approval.
 */
const TYPE_TONE: Record<SignatureLevel, 'neutral' | 'accent' | 'success'> = {
  simple: 'neutral',
  advanced: 'accent',
  qualified: 'success',
};

function formatSigningDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const dividerColor = gaiaNeutral.borderSubtle;
const faintColor = gaiaNeutral.textFaint;
</script>

<template>
  <n-flex v-if="signatures.length" vertical :size="8" :wrap="false">
    <gaia-section-header>
      {{ signatures.length === 1 ? 'Signature' : `${signatures.length} signatures` }}
    </gaia-section-header>

    <n-flex
      v-for="(sig, index) in signatures"
      :key="sig.uid"
      vertical
      :size="6"
      :wrap="false"
      class="entry"
      :class="{ divided: index > 0 }"
    >
      <n-flex align="center" :size="8" :wrap="false">
        <gaia-status-chip :tone="TYPE_TONE[sig.type]">
          {{ TYPE_LABEL[sig.type] }}
        </gaia-status-chip>
        <gaia-text variant="label" size="nano" tone="faint">eSignature</gaia-text>
      </n-flex>

      <div class="rows">
        <gaia-text size="control" tone="tertiary">Signer</gaia-text>
        <gaia-text size="control" weight="medium" tone="ink">{{ sig.signer || '—' }}</gaia-text>

        <gaia-text size="control" tone="tertiary">Authority</gaia-text>
        <gaia-text size="control" weight="medium" tone="ink">
          {{ sig.authority || '—' }}
        </gaia-text>

        <gaia-text size="control" tone="tertiary">Signed on</gaia-text>
        <gaia-text size="control" weight="medium" tone="ink">
          {{ formatSigningDate(sig.signing_date) }}
        </gaia-text>
      </div>
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* A dashed rule between entries; no theme var draws a divider inside a flex
   column. */
.entry.divided {
  padding-top: 10px;
  border-top: 1px dashed v-bind(dividerColor);
}

/* Label / value pairs on one grid so the values line up down the card. */
.rows {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  color: v-bind(faintColor);
}
</style>
